/**
 * suggestionEngine.js
 * ===================
 * Pure in-memory suggestion engine — zero network calls, zero latency.
 *
 * Uses data already loaded by useWorkspaceBootstrap:
 *   • synonyms       — measure aliases, dimension aliases, value aliases
 *   • metadata       — all table columns and measure names from the model
 *   • businessSemantic — KPI names, display names, synonyms
 *
 * Returns suggestions in the same shape the backend /api/suggestions used to:
 *   { kind, label, insert_text, canonical_name, matched_on, score }
 *
 * Scoring:
 *   100  exact match
 *    90  prefix match on primary name
 *    80  prefix match on alias / synonym
 *    65  substring match on primary name
 *    50  substring match on alias
 */

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalize(s) {
  return (s || '').toLowerCase().trim()
}

/** Extract human-readable column label from "table[Column]" or "table.Column" */
function columnLabel(canonical) {
  const m = canonical.match(/\[([^\]]+)\]$/)
  return m ? m[1] : canonical.split('.').pop()
}

/** Score a candidate string against a query token */
function scoreMatch(candidate, query) {
  const c = normalize(candidate)
  const q = normalize(query)
  if (!c || !q) return 0
  if (c === q)            return 100
  if (c.startsWith(q))   return 90
  if (c.includes(q))     return 65
  return 0
}

/** Score across primary name + all aliases, return best */
function bestScore(primaryName, aliases, query) {
  let best = scoreMatch(primaryName, query)
  for (const alias of (aliases || [])) {
    const s = scoreMatch(alias, query)
    // Alias matches are slightly lower priority than primary
    if (s === 100) best = Math.max(best, 99)
    else if (s === 90) best = Math.max(best, 80)
    else if (s > 0) best = Math.max(best, 50)
  }
  return best
}

// ── Builder ───────────────────────────────────────────────────────────────────

/**
 * Build a suggest(token, limit) function from the already-loaded frontend data.
 *
 * Call once when data loads, then call suggest() on every keystroke.
 *
 * @param {object} synonyms       - from api.getSynonyms()
 * @param {object} metadata       - from api.getMetadata()
 * @param {object} businessSemantic - from api.getBusinessSemantic()
 * @returns {function} suggest(token: string, limit?: number) → SuggestionItem[]
 */
export function buildSuggestionEngine(synonyms, metadata, businessSemantic) {
  // ── Pre-process once ────────────────────────────────────────────────────────
  const candidates = []

  // 1. Measure synonyms  { "Total Sales": ["sales", "revenue", ...] }
  const measureSyns = synonyms?.measures || {}
  for (const [measureName, aliases] of Object.entries(measureSyns)) {
    candidates.push({
      kind:           'measure',
      primary:        measureName,
      aliases:        aliases || [],
      insert_text:    measureName,
      canonical_name: `measure:${measureName}`,
      labelPrefix:    '',
    })
  }

  // 2. Dimension synonyms  { "m_division[DIVISION]": ["division", "div", ...] }
  const dimSyns = synonyms?.dimensions || {}
  for (const [canonical, aliases] of Object.entries(dimSyns)) {
    const label = columnLabel(canonical)
    candidates.push({
      kind:           'dimension',
      primary:        label,
      aliases:        aliases || [],
      insert_text:    label,
      canonical_name: canonical,
      labelPrefix:    'Dimension: ',
    })
  }

  // 3. Value synonyms  { "m_division[DIVISION]": { "PHARMA": ["pharma", ...] } }
  const valueSyns = synonyms?.values || {}
  for (const [fieldCanonical, valueMap] of Object.entries(valueSyns)) {
    const fieldLabel = columnLabel(fieldCanonical)
    for (const [value, aliases] of Object.entries(valueMap || {})) {
      candidates.push({
        kind:           'value',
        primary:        value,
        aliases:        aliases || [],
        insert_text:    value,
        canonical_name: `${fieldCanonical}=${value}`,
        labelPrefix:    `${fieldLabel}: `,
      })
    }
  }

  // 4. Business semantic KPIs  items[].Display.name + items[].Synonym
  const bsItems = businessSemantic?.items || []
  for (const item of bsItems) {
    const displayName = item?.Display?.name || ''
    if (!displayName) continue
    const bsSynonyms = item?.Synonym || []
    const description = item?.Description ? ` — ${item.Description.slice(0, 60)}` : ''
    candidates.push({
      kind:           'measure',
      primary:        displayName,
      aliases:        bsSynonyms,
      insert_text:    displayName,
      canonical_name: `kpi:${displayName}`,
      labelPrefix:    '',
      description,
    })
  }

  // 5. Metadata — all non-hidden measure names from all tables
  const tables = metadata?.tables || []
  const seenMeasures = new Set(Object.keys(measureSyns))
  for (const table of tables) {
    for (const measure of (table.measures || [])) {
      const name = measure.name || ''
      if (!name || seenMeasures.has(name)) continue
      seenMeasures.add(name)
      candidates.push({
        kind:           'measure',
        primary:        name,
        aliases:        [],
        insert_text:    name,
        canonical_name: `${table.name}[${name}]`,
        labelPrefix:    '',
      })
    }
  }

  // 6. Metadata — visible dimension columns from dimension tables
  const seenDims = new Set(Object.keys(dimSyns))
  for (const table of tables) {
    if (!table.isDimension) continue
    for (const col of (table.columns || [])) {
      if (col.isHidden) continue
      const canonical = `${table.name}[${col.name}]`
      if (seenDims.has(canonical)) continue
      seenDims.add(canonical)
      candidates.push({
        kind:           'dimension',
        primary:        col.name,
        aliases:        [],
        insert_text:    col.name,
        canonical_name: canonical,
        labelPrefix:    `${table.name}: `,
      })
    }
  }

  // ── Deduplicate by (kind + insert_text) ──────────────────────────────────
  const seen = new Set()
  const unique = candidates.filter(c => {
    const key = `${c.kind}::${normalize(c.insert_text)}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  // ── suggest function ───────────────────────────────────────────────────────
  return function suggest(token, limit = 8) {
    if (!token || token.length < 2) return []

    const results = []
    for (const c of unique) {
      const score = bestScore(c.primary, c.aliases, token)
      if (score > 0) {
        results.push({
          kind:           c.kind,
          label:          `${c.labelPrefix}${c.primary}`,
          insert_text:    c.insert_text,
          canonical_name: c.canonical_name,
          matched_on:     token,
          score,
        })
      }
    }

    // Sort: score desc, then alphabetically by label
    results.sort((a, b) =>
      b.score !== a.score
        ? b.score - a.score
        : a.label.localeCompare(b.label)
    )

    return results.slice(0, limit)
  }
}
